import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@mui/lab';
import { TextField, MenuItem, IconButton } from '@mui/material';
import moment from 'moment';
import { useSelector } from 'react-redux';
import DeleteIcon from '@mui/icons-material/Delete';

import ButtonWithLoader from '../../../../components/other/Buttons/ButtonWithLoader';
import { getOrderTypeWithTranslates } from '../constatns';
import { useStyles } from '../hooks/useStyles';
import { IOrderResponse } from '../../../../interfaces/order';
import { DriverModal } from './DriverModal';
import { BuyerModal } from './BuyerModal';
import { useGetPriceRoute } from './hooks/getPriceRoute';
import { useYamap } from './hooks/useYamap';
import { LocationsOrder } from './LocationsOrder';
import { useGetLastLocations } from './hooks/useGetLastLocations';
import AlertDialog from '../../../../components/other/Dialog/AlertDialog';
import { AutocompleteBuyer } from './AutocompleteBuyer';
import { IAppState } from '../../../../store/rootDuck';
import { useDefineUserRole } from '../../../../hooks';
import NumberFormat from '../../../../components/NumberFormat';

export enum ViewMode {
  VIEW = 'view',
  EDIT = 'edit',
}

export function OrderForm({
  formik,
  disabled,
  order,
  loading,
  editLoading,
  onSubmit,
  isEdit,
}: {
  formik: any;
  disabled: boolean;
  order?: IOrderResponse;
  loading?: boolean;
  editLoading?: boolean;
  onSubmit: () => void;
  isEdit?: boolean;
}) {
  const [showDriverModal, setDriverModal] = useState(false);
  const [showBuyerModal, setBuyerModal] = useState(false);
  const [isAlertOpen, setAlertOpen] = useState(false);
  const { me } = useSelector((store: IAppState) => ({
    me: store.profile.me,
  }));
  const isOper = useDefineUserRole(me, 'ROLE_ORDER_OPERATOR');
  const { getLocations, locations } = useGetLastLocations();
  const { handleOrderSumm } = useYamap({
    startLocation: formik.values.startLocation,
    endLocation: formik.values.endLocation,
    setTime: time => formik.setFieldValue('time', time),
    setLength: length => formik.setFieldValue('length', length),
  });
  const { fetchPriceTariff, prices, clear } = useGetPriceRoute(prices => {
    if (!prices?.[0]) return;
    if (!isEdit && prices?.[0]) {
      formik.setFieldValue('tariff', prices[0]);
    }
    if (!values.calcSumm || !prices.find(i => i.cost === values.calcSumm)) {
      formik.setFieldValue('calcSumm', prices[0].cost);
    }
  });

  const intl = useIntl();
  const navigate = useNavigate();
  const { classes } = useStyles();

  const { values, handleBlur, handleChange, errors, touched, setFieldValue } = formik;

  const translates: { [type: string]: string } = {
    formed: intl.formatMessage({ id: 'ORDER.TYPE.CART' }),
    new: intl.formatMessage({ id: 'ORDER.TYPE.NEW' }),
    driver_found: intl.formatMessage({ id: 'ORDER.TYPE.FORMATION' }),
    driver_waiting: intl.formatMessage({ id: 'ORDER.TYPE.DRIVER_WAITING' }),
    driver_coming: intl.formatMessage({ id: 'ORDER.TYPE.DELIVERY' }),
    way: intl.formatMessage({ id: 'ORDER.TYPE.PAYED' }),
    completed: intl.formatMessage({ id: 'ORDER.TYPE.COMPLETED' }),
    canceled: intl.formatMessage({ id: 'ORDER.TYPE.CANCELED' }),
  };
  const orderTypes = getOrderTypeWithTranslates(translates, true);

  const completeOrder = () => {
    setFieldValue('status', 'completed');
  };

  useEffect(() => {
    if (values.user?.id) {
      getLocations({ userId: values.user.id });
    }
  }, [values.user]);

  useEffect(() => {
    if (!isEdit) {
      handleOrderSumm();
    }
  }, [
    isEdit,
    values.startLocation?.lat,
    values.endLocation?.lat,
    values.startLocation?.lng,
    values.endLocation?.lng,
    values.startLocation?.address,
    values.endLocation?.address,
  ]);

  useEffect(() => {
    if (values.time && values.length) {
      fetchPriceTariff({
        length: values.length,
        time: values.time,
        province1: values.startLocation.province || undefined,
        province2: values.endLocation.province || undefined,
      });
    }
    if (!values.time || !values.length) {
      clear();
      formik.setFieldValue('tariff', null);
      formik.setFieldValue('calcSumm', 0);
    }
  }, [values.time, values.length]);

  return (
    <>
      {loading ? (
        <>
          <Skeleton width='100%' height={70} animation='wave' />
          <Skeleton width='100%' height={70} animation='wave' />
          <Skeleton width='100%' height={70} animation='wave' />
          <Skeleton width='100%' height={70} animation='wave' />
          <Skeleton width='100%' height={70} animation='wave' />
          <Skeleton width='100%' height={70} animation='wave' />
          <Skeleton width='100%' height={70} animation='wave' />
        </>
      ) : (
        <>
          <AutocompleteBuyer formik={formik} />

          {/* <div className={classes.companySearch}>
            <TextField
              type='text'
              label={intl.formatMessage({ id: 'ORDER.TABLE.BUYER.INFO' })}
              margin='normal'
              name='user'
              value={`${values.user?.fio ? `${values.user?.fio},` : ''} ${
                values.user?.phone ? `+${values.user?.phone}` : ''
              }`}
              variant='outlined'
              helperText={errors.user?.id || errors.user}
              error={Boolean(errors.user?.id || errors.user)}
              onBlur={handleBlur}
              onChange={handleChange}
              disabled={disabled}
              inputProps={{ readOnly: true }}
            />
            {!disabled && (
              <div
                className={classes.containerAll}
                style={{
                  marginBottom: errors.user?.id || errors.user ? 18 : 0,
                }}
              >
                <ButtonWithLoader
                  height={52}
                  onPress={() => {
                    setBuyerModal(true);
                  }}
                >
                  {intl.formatMessage({ id: 'BUYERS.ALL' })}
                </ButtonWithLoader>
              </div>
            )}
          </div> */}
          {order && (
            <>
              <TextField
                margin='normal'
                label={`${intl.formatMessage({ id: 'ORDER.TABLE.ID' })}`}
                value={order?.id}
                variant='outlined'
                inputProps={{ readOnly: true }}
              />
              <TextField
                margin='normal'
                label={`${intl.formatMessage({ id: 'ORDER.DATETIME' })}`}
                value={`${
                  order?.created_at ? moment(order?.created_at).format('DD.MM.YYYY HH:mm') : ''
                }`}
                variant='outlined'
                inputProps={{ readOnly: true }}
              />
            </>
          )}
          <LocationsOrder
            formik={formik}
            disabled={disabled}
            lastLocations={locations}
            setStart={location => isEdit && handleOrderSumm({ newStartLocation: location })}
            setEnd={location => isEdit && handleOrderSumm({ newEndLocation: location })}
          />
          <TextField
            type='text'
            label={intl.formatMessage({ id: 'COMMENT.ORDER' })}
            margin='normal'
            name='comment'
            value={values.comment}
            variant='outlined'
            helperText={touched.comment && errors.comment?.toString()}
            error={Boolean(touched.comment && errors.comment)}
            onBlur={handleBlur}
            onChange={handleChange}
            disabled={disabled}
          />
          {order && !disabled && (
            <TextField
              type='text'
              label={intl.formatMessage({ id: 'ORDER.TABLE.DRIVER.COMMENT' })}
              margin='normal'
              name='commentDriver'
              value={values.commentDriver}
              variant='outlined'
              helperText={touched.commentDriver && errors.commentDriver?.toString()}
              error={Boolean(touched.commentDriver && errors.commentDriver)}
              onBlur={handleBlur}
              onChange={handleChange}
              disabled={disabled}
            />
          )}
          <div className={classes.companySearch}>
            <TextField
              type='number'
              label={intl.formatMessage({ id: 'ORDER.CALC_SUMM' })}
              margin='normal'
              name='calcSumm'
              value={values.calcSumm}
              variant='outlined'
              helperText={touched.calcSumm && errors.calcSumm?.toString()}
              error={Boolean(touched.calcSumm && errors.calcSumm)}
              onBlur={handleBlur}
              onChange={handleChange}
              disabled={disabled}
              inputProps={{
                readOnly: true,
              }}
            />
            {!disabled && (
              <div className={classes.containerAll}>
                <ButtonWithLoader height={52} onPress={handleOrderSumm}>
                  {intl.formatMessage({ id: 'RECALCULATE' })}
                </ButtonWithLoader>
              </div>
            )}
          </div>
          <TextField
            type='text'
            label={intl.formatMessage({ id: 'ORDER.FIX_SUMM' })}
            margin='normal'
            name='fixSumm'
            value={values.fixSumm}
            variant='outlined'
            helperText={touched.fixSumm && errors.fixSumm?.toString()}
            error={Boolean(touched.fixSumm && errors.fixSumm)}
            onBlur={handleBlur}
            onChange={handleChange}
            disabled={disabled}
            InputProps={{
              inputComponent: NumberFormat as any,
            }}
          />
          <TextField
            type='number'
            label={intl.formatMessage({ id: 'ORDER.SUMM' })}
            margin='normal'
            name='summ'
            value={
              !order
                ? values.summ || values.fixSumm || values.calcSumm
                : values.fixSumm || values.calcSumm || values.summ
            }
            variant='outlined'
            helperText={touched.summ && errors.summ?.toString()}
            error={Boolean(touched.summ && errors.summ)}
            onBlur={handleBlur}
            onChange={handleChange}
            disabled={disabled}
            inputProps={{
              readOnly: true,
            }}
          />
          <div className={classes.companySearch}>
            <TextField
              type='text'
              label={intl.formatMessage({ id: 'ORDER.TABLE.DRIVER.INFO' })}
              margin='normal'
              name='driver'
              value={`${values.driver?.fio ? `${values.driver?.fio},` : ''} ${
                values.driver?.phone ? `+${values.driver?.phone}` : ''
              }`}
              variant='outlined'
              helperText={touched.driver && errors.driver?.toString()}
              error={Boolean(touched.driver && errors.driver)}
              onBlur={handleBlur}
              onChange={handleChange}
              disabled={disabled}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <>
                    {values.driver && !disabled && (
                      <IconButton onClick={() => setFieldValue('driver', undefined)}>
                        <DeleteIcon color='error' fontSize='small' />
                      </IconButton>
                    )}
                  </>
                ),
              }}
            />
            {!disabled && (
              <div className={classes.containerAll}>
                <ButtonWithLoader
                  height={52}
                  onPress={() => {
                    setDriverModal(true);
                  }}
                >
                  {intl.formatMessage({ id: 'DRIVERS.ALL' })}
                </ButtonWithLoader>
              </div>
            )}
          </div>
          {order && (
            <TextField
              select
              margin='normal'
              label={`${intl.formatMessage({ id: 'ORDER.INPUT.STATUS' })}`}
              value={values.status || ''}
              onChange={e => {
                if (e.target.value === 'completed') {
                  setAlertOpen(true);
                } else {
                  setFieldValue('status', e.target.value);
                }
              }}
              name='status'
              variant='outlined'
              helperText={touched.status && errors.status?.toString()}
              error={Boolean(touched.status && errors.status)}
              disabled={disabled}
            >
              {orderTypes.map(item => (
                <MenuItem value={item.type}>{item.translate}</MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            select
            margin='normal'
            label={`${intl.formatMessage({ id: 'USER.TABLE.TARIFF' })}`}
            value={prices.find(item => item.name === values.tariff?.name)?.id || ''}
            onChange={e => {
              const tariff = prices.find(
                item => item.id.toString() === e.target.value.toString()
              );
              if (tariff) {
                setFieldValue('tariff', tariff);
                setFieldValue('calcSumm', tariff.cost);
              }
            }}
            name='tariff'
            variant='outlined'
            disabled={disabled}
          >
            {prices.map(item => (
              <MenuItem value={item.id}>{`${item.name} - ${item.cost} руб.`}</MenuItem>
            ))}
          </TextField>

          {!disabled && (
            <div className={classes.bottomActions}>
              <ButtonWithLoader
                onPress={() => navigate(-1)}
                variant='contained'
                color='secondary'
                type='button'
              >
                {intl.formatMessage({ id: 'COMMON.BUTTON.CLOSE' })}
              </ButtonWithLoader>
              <ButtonWithLoader
                marginLeft={10}
                disabled={editLoading || (!values.time && !values.length)}
                loading={editLoading}
                onPress={onSubmit}
              >
                {intl.formatMessage({
                  id: order ? 'FORM.SAVE' : 'ORDER.BUTTON.CREATE',
                })}
              </ButtonWithLoader>
            </div>
          )}
        </>
      )}

      <DriverModal
        showDriverModal={showDriverModal}
        closeDriverModal={() => setDriverModal(false)}
        setFieldValue={setFieldValue}
        values={values}
      />

      <BuyerModal
        showDriverModal={showBuyerModal}
        closeDriverModal={() => setBuyerModal(false)}
        setFieldValue={setFieldValue}
        values={values}
      />
      <AlertDialog
        open={isAlertOpen}
        message={intl.formatMessage({ id: 'ORDER.COMPLETE.STATUS.ALERT' })}
        okText={intl.formatMessage({ id: 'CATEGORIES.DELETE.OK' })}
        cancelText={intl.formatMessage({ id: 'CATEGORIES.DELETE.CANCEL' })}
        handleClose={() => {
          setAlertOpen(false);
        }}
        handleAgree={() => completeOrder()}
      />
    </>
  );
}
